import React, { useState } from "react";
import { BotType, LanguageNameSpaces } from "@shared/types";
import { useHistory } from "react-router-dom";
import EmptyView from "common/Empty/EmptyView";
import {
  DCA_PUBLIC_BOTS_PATH,
  IS_WHOP,
  MOBILE_SEARCH,
  TABLET,
} from "common/constants";
import {
  getCreateBotRoute,
  getCreateChannelConfigurationsPath,
} from "utils/routes";
import {
  BOT_TYPE_TO_DISPLAY_NAME,
  BOT_TYPE_TO_FILTERS_SLICE,
} from "@shared/features/bots/constants";
import { useSelector } from "react-redux";
import NoExchangeConnectedModal from "features/configuration/common/NoExchangeConnectedModal";
import { groupConfigurationsFilters } from "@shared/slices/filters.slice";
import useHistoryWithQuery from "utils/hooks/useHistoryWithQuery";
import { sharedLib } from "@shared/services/shared.service";
import useMedia from "utils/hooks/useMedia.ts";
import { EmptyStatesType } from "common/Icons/types.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { useCanManageBotType } from "@shared/features/bots/hooks";

interface BotsEmptyStateProps {
  botType: BotType;
  hasData: boolean;
  isSearch?: boolean;
  openFilters: () => void;
  isChannelConfiguration?: boolean;
}

export const BotsEmpty = ({
  botType,
  isSearch,
  hasData,
  openFilters,
  isChannelConfiguration,
}: BotsEmptyStateProps) => {
  const history = useHistory();
  const isDca = botType === BotType.ASAP;
  const user = useSelector(sharedLib.selectors.authentication.selectUser);
  const [noExchangeModalVisible, setNoExchangeModalVisible] = useState(false);
  const { historyPush } = useHistoryWithQuery();
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const canManageBotType = useCanManageBotType(botType);

  const onClick = () => {
    if (!isDca && !user?.has_valid_clients_connected && !IS_WHOP) {
      setNoExchangeModalVisible(true);
    } else if (isChannelConfiguration) {
      historyPush(getCreateChannelConfigurationsPath());
    } else {
      history.push(isDca ? DCA_PUBLIC_BOTS_PATH : getCreateBotRoute(botType));
    }
  };

  const objectName = t(BOT_TYPE_TO_DISPLAY_NAME[botType]);
  const filtersSlice = isChannelConfiguration
    ? groupConfigurationsFilters
    : BOT_TYPE_TO_FILTERS_SLICE[botType];
  const channelConfigurationObjectName = t("labels.group_configuration");
  const title = isChannelConfiguration
    ? t("empty_states.no_group_configurations")
    : t("empty_states.no_bots_of_type", { botType: objectName });
  const buttonText = canManageBotType
    ? isChannelConfiguration
      ? t("buttons.create_a_bot", { botType: channelConfigurationObjectName })
      : isDca
      ? t("buttons.copy_a_bot", { botType: objectName })
      : t("buttons.create_a_bot", { botType: objectName })
    : undefined;

  return (
    <>
      <EmptyView
        iconKey={EmptyStatesType.Bots}
        iconWidth={isMobile ? 120 : 200}
        title={title}
        description={
          isDca
            ? t("empty_states.find_bot_to_copy", { botType: objectName })
            : undefined
        }
        buttonText={buttonText}
        buttonOnClick={canManageBotType ? onClick : undefined}
        objectName={`${
          isChannelConfiguration
            ? channelConfigurationObjectName
            : `${objectName} bots`
        }`}
        filtersSlice={filtersSlice}
        isSearch={isSearch}
        openFilters={openFilters}
        hasData={hasData}
        filtersChipsOpen={TABLET}
      />
      <NoExchangeConnectedModal
        placement="bots_empty"
        onClose={() => setNoExchangeModalVisible(false)}
        action={t("buttons.create_bot")}
        open={noExchangeModalVisible}
      />
    </>
  );
};

export default BotsEmpty;
